// filepath: /home/test/Desktop/rpm/frontend/src/app/modules/user/components/modals/CreateDeviceModal.tsx
import React, {useState} from 'react'
import {Formik, Form, Field, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiFileText,
  FiUploadCloud,
  FiPlusCircle,
} from 'react-icons/fi'
import {FaTimesCircle} from 'react-icons/fa'
import {CreateDeviceFormValuesModel} from '../../../../entities/component.entity'
import {DeviceTypeModel} from '../../../../entities/device.entity'
import Astericsign from '../forms/Astericsign'
import {
  imeiNumberValidation,
  modelNumberValidation,
} from '../forms/helper/ValidationHelperComponent'
import PrimaryButton from '../common/buttons/PrimaryButton'
import {Modal} from 'react-bootstrap'

interface CreateDeviceModalProps {
  isOpen: boolean
  onClose: () => void
  handleSubmit: (values: CreateDeviceFormValuesModel, resetForm: () => void) => void
  deviceTypes: DeviceTypeModel[]
  handleCSVUpload: (file: File | null) => void
  uploadReport: {createdCount: number; skippedCount: number} | null
  isDeviceCreationLoading: boolean
  isCSVUploading: boolean
  setFormDirty?: (isDirty: boolean) => void
  formDirty?: boolean
  showDiscardModal?: boolean
  handleModalClose?: () => void
  discardChanges?: () => void
  keepEditing?: () => void
}

const CreateDeviceModal: React.FC<CreateDeviceModalProps> = ({
  isOpen,
  onClose,
  handleSubmit,
  deviceTypes,
  handleCSVUpload,
  uploadReport,
  isDeviceCreationLoading,
  isCSVUploading,
  setFormDirty,
  formDirty,
  showDiscardModal,
  handleModalClose,
  discardChanges,
  keepEditing,
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null)

  const validationSchema = Yup.object({
    name: Yup.string().trim().required('Device Name is required'),
    deviceType: Yup.string().trim().required('Device Type is required'),
    imeiNumber: imeiNumberValidation,
    modelNumber: modelNumberValidation,
    ownershipType: Yup.string().oneOf(['loaned', 'rental', 'purchased']).required('Ownership is required'),
  })

  const initialValues: CreateDeviceFormValuesModel = {
    name: '',
    deviceType: deviceTypes[0]?.id ? String(deviceTypes[0].id) : '',
    imeiNumber: '',
    modelNumber: 'TMB-2092-Ga',
    serialNumber: '',
    ownershipType: 'loaned',
  }

  // Normalize IMEI on blur: strip non-digits, and if the value is exactly 14 digits
  // starting with a non-zero, prepend "0" (recovers leading zeros stripped by Excel).
  // Also derives the device name as "CANDI" + last 6 IMEI digits.
  const handleImeiBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void,
  ) => {
    let val = (e.currentTarget.value || '').replace(/\D/g, '')
    if (val.length === 14 && val[0] !== '0') {
      val = '0' + val
    }
    setFieldValue('imeiNumber', val)
    if (val.length >= 6) {
      setFieldValue('name', `CANDI${val.slice(-6)}`)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className={`modal fade show d-block bg-dark bg-opacity-50 ${
        showDiscardModal ? 'modal-stacked' : ''
      }`}
      tabIndex={-1}
      role='dialog'
    >
      <div className='modal-dialog modal-dialog-centered modal-lg' role='document'>
        <div className='modal-content'>
          {/* Header */}
          <div className='modal-header text-black'>
            <h5 className='modal-title d-flex align-items-center gap-2 text-black'>
              <FiPlusCircle size={20} /> Add Device
            </h5>
            <button
              type='button'
              className='btn-close btn-close'
              onClick={handleModalClose || onClose}
            ></button>
          </div>

          {/* Body */}
          <div className='modal-body' style={{maxHeight: '500px', overflowY: 'auto'}}>
            {/* Manual Creation */}
            <div className='mb-4 p-4 rounded-3 shadow-sm bg-light'>
              <h6 className='fw-bold d-flex align-items-center gap-2 mb-3'>
                <FiFileText className='text-primary' /> Create Manually
              </h6>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, {resetForm}) => handleSubmit(values, resetForm)}
              >
                {({dirty, setFieldValue}) => {
                  // Update form dirty state whenever it changes
                  if (setFormDirty && dirty !== formDirty) {
                    setFormDirty(dirty)
                  }

                  return (
                    <Form>
                      <div className='row g-3'>
                        {/* Device Type */}
                        <div className='col-sm-6'>
                          <label className='form-label'>
                            Device Type <Astericsign />
                          </label>
                          <Field as='select' name='deviceType' className='form-select'>
                            <option value=''>Select device</option>
                            {deviceTypes.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name='deviceType'
                            component='div'
                            className='text-danger mt-1'
                          />
                        </div>

                        {/* IMEI Number */}
                        <div className='col-sm-6'>
                          <label className='form-label'>
                            IMEI Number <Astericsign />
                          </label>
                          <Field
                            type='text'
                            name='imeiNumber'
                            className='form-control'
                            maxLength={15}
                            inputMode='numeric'
                            title='15-digit IMEI'
                            onInput={(e: any) =>
                              (e.currentTarget.value = e.currentTarget.value.replace(/\D/g, ''))
                            }
                            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                              handleImeiBlur(e, setFieldValue)
                            }
                          />
                          <ErrorMessage
                            name='imeiNumber'
                            component='div'
                            className='text-danger mt-1'
                          />
                        </div>

                        {/* Device Name */}
                        <div className='col-sm-6'>
                          <label className='form-label'>Device Name <Astericsign /></label>
                          <Field type='text' name='name' className='form-control' />
                           <ErrorMessage
                            name='name'
                            component='div'
                            className='text-danger mt-1'
                          />
                        </div>

                        {/* Model Number */}
                        <div className='col-sm-6'>
                          <label className='form-label'>
                            Model Number <Astericsign />
                          </label>
                          <Field type='text' name='modelNumber' className='form-control' />
                          <ErrorMessage
                            name='modelNumber'
                            component='div'
                            className='text-danger mt-1'
                          />
                        </div>

                        {/* Serial Number (optional) */}
                        <div className='col-sm-6'>
                          <label className='form-label'>Serial Number</label>
                          <Field
                            type='text'
                            name='serialNumber'
                            className='form-control'
                            placeholder='Optional'
                          />
                          <ErrorMessage
                            name='serialNumber'
                            component='div'
                            className='text-danger mt-1'
                          />
                        </div>

                        {/* Ownership Type */}
                        <div className='col-sm-6'>
                          <label className='form-label'>
                            Ownership <Astericsign />
                          </label>
                          <Field as='select' name='ownershipType' className='form-select'>
                            <option value='loaned'>Loaned</option>
                            <option value='rental'>Rented</option>
                            <option value='purchased'>Owned</option>
                          </Field>
                          <ErrorMessage
                            name='ownershipType'
                            component='div'
                            className='text-danger mt-1'
                          />
                        </div>
                      </div>

                      <div className='mt-4 text-end d-flex justify-content-end'>
                        <PrimaryButton type='submit' disabled={isDeviceCreationLoading}>
                          {isDeviceCreationLoading ? 'Adding...' : 'Add Device'}
                        </PrimaryButton>
                      </div>
                    </Form>
                  )
                }}
              </Formik>
            </div>

            {/* Divider */}
            <hr className='my-4' style={{borderStyle: 'dashed', borderColor: '#ddd'}} />

            {/* CSV Upload */}
            <div className='p-4 rounded-3 shadow-sm bg-light'>
              <h6 className='fw-bold d-flex align-items-center gap-2 mb-3'>
                <FiFileText className='text-primary' /> Or upload via CSV
              </h6>
              <div className='row align-items-center'>
                <div className='col-sm-9 mb-3'>
                  <label
                    htmlFor='csvUpload'
                    className='d-flex align-items-center gap-2 p-2 rounded w-100'
                    style={{border: '2px dotted #ccc', cursor: 'pointer'}}
                  >
                    <FiUploadCloud size={24} className='text-secondary' />
                    <span className='text-muted'>{csvFile?.name ?? 'Select a CSV file...'}</span>
                    <input
                      id='csvUpload'
                      type='file'
                      accept='.csv'
                      hidden
                      onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                    />
                  </label>
                </div>

                <div className='col-sm-3 mb-3'>
                  <div className='d-grid'>
                    <PrimaryButton
                      type='button'
                      onClick={() => handleCSVUpload(csvFile)}
                      disabled={isCSVUploading}
                    >
                      {isCSVUploading ? (
                        'Uploading...'
                      ) : (
                        <>
                          <FiUploadCloud className='me-2' /> Upload CSV
                        </>
                      )}
                    </PrimaryButton>
                  </div>
                </div>
              </div>

              {uploadReport && (
                <div className='mt-3 d-flex gap-4'>
                  <p className='d-flex align-items-center mb-0 text-success gap-1'>
                    <FiCheckCircle /> Created: {uploadReport.createdCount}
                  </p>
                  <p className='d-flex align-items-center mb-0 text-warning gap-1'>
                    <FiAlertTriangle /> Skipped: {uploadReport.skippedCount}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Discard changes confirmation modal */}
      {showDiscardModal && (
        <Modal show={showDiscardModal} onHide={keepEditing} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FaTimesCircle className='text-danger me-2' />
              Unsaved Changes
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            You have entered information that hasn't been saved yet. Are you sure you want to
            discard your changes?
          </Modal.Body>
          <Modal.Footer>
            <PrimaryButton color='secondary' onClick={keepEditing}>
              Keep Editing
            </PrimaryButton>
            <PrimaryButton color='danger' onClick={discardChanges}>
              Discard Changes
            </PrimaryButton>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}

export default CreateDeviceModal
